import React, { useEffect, useState } from "react";
import { useToast } from "../../@/components/ui/use-toast";
import { usePayoutToVendorMutation } from "../../api/api";
import { useNavigate } from "react-router-dom";
import { FiEdit } from "react-icons/fi";
import {
  MdOutlinePayment,
  MdOutlineLocalPhone,
  MdAccountBalance,
  MdOutlineAccountBox,
  MdRoute,
  MdOutlineStorefront,
  MdOutlineDateRange,
} from "react-icons/md";
import SortupDialog from "../../components/common/SortupDialog";
import { formatCurrency } from "../../lib/utils";

interface PayoutInvoiceDialogProps {
  selectedRows: Array<{
    id: number;
    vendorId: number;
    statusId: number;
    createdAt: string;
    totalAmount: number;
    totalOrderAmount: number;
    payoutDateTime: string | null;
    deliveryCharges: number;
    serviceFee: number | null;
    payoutId: number | null;
    vendorName: string;
    statusName: string;
    paymentProcessingFee: number | null;
    oldDeliveryCharges: number | null;
    oldTotalAmount: number | null;
    couponVendorId: number | null;
    discountAmount: number | null;
  }>;
  selectedVendor: {
    id: number;
    company: string;
    countryCode: string;
    phone: string;
    SSN: string;
    merchantCode: string;
    accountHolderName: string;
    bankName: string;
    accountNumber: string;
    routingNumber: string;
    swiftCode: string;
    DOB: Date;
  };
  handleGetSelectedRows: () => void;
  isDialogOpen: boolean;
  setIsDialogOpen: React.Dispatch<React.SetStateAction<boolean>>;
  fetchOrderData: any;
}

const PayoutInvoiceDialog: React.FC<PayoutInvoiceDialogProps> = ({
  selectedRows,
  selectedVendor,
  handleGetSelectedRows,
  isDialogOpen,
  setIsDialogOpen,
  fetchOrderData,
}) => {
  const [vendorInfo, setVendorInfo] = useState(selectedVendor);
  const { toast } = useToast();
  const [payoutToVendor, { isLoading: isPayoutLoading }] =
    usePayoutToVendorMutation();
  const navigate = useNavigate();

  useEffect(() => {
    setVendorInfo(selectedVendor);
  }, [selectedVendor, isDialogOpen]);

  const handlePay = async () => {
    const ids = selectedRows?.map((row) => row.id);
    const response = await payoutToVendor({
      payoutToVendorDto: {
        orderIds: ids,
      },
    }).unwrap();
    fetchOrderData();
    toast({
      title: "Payout",
      description: response?.message || "",
    });
    setIsDialogOpen(false);
  };

  const handleCancel = () => {
    setIsDialogOpen(false);
  };

  const handleIconClick = (vendorId: number) => {
    navigate(`/store?vendorId=${vendorId}&tab=4`);
  };

  // Calculate totals
  const totalOrderAmount = selectedRows?.reduce(
    (acc, row) => acc + (row.totalOrderAmount || 0),
    0
  );
  const totalAmount =
    selectedRows?.reduce(
      (acc, row) =>
        acc +
        (row.couponVendorId === 0 || row.couponVendorId === null
          ? row.oldTotalAmount || row.totalAmount
          : row.totalAmount ?? 0),
      0
    ) ?? 0;

  const totalDiscountAmount =
    selectedRows?.reduce(
      (acc, row) =>
        acc +
        (row.couponVendorId === 0 || row.couponVendorId === null
          ? 0
          : row.discountAmount ?? 0),
      0
    ) ?? 0;

  const totalServiceFee = selectedRows?.reduce(
    (acc, row) => acc + (row.serviceFee || 0),
    0
  );
  const totalDeliveryCharges = selectedRows?.reduce(
    (acc, row) => acc + (row.deliveryCharges || 0),
    0
  );
  const totalPaymentProcessingFee = selectedRows?.reduce(
    (acc, row) => acc + (row.paymentProcessingFee || 0),
    0
  );
  const totalPayoutAmount = totalAmount;
  const totalOrderAmountWithDelivery = totalOrderAmount + totalDeliveryCharges;

  // Add this function to check if all required fields are filled
  const isVendorInfoComplete = () => {
    return !!(
      vendorInfo?.company &&
      vendorInfo?.accountHolderName &&
      vendorInfo?.bankName &&
      vendorInfo?.accountNumber &&
      vendorInfo?.phone &&
      vendorInfo?.DOB &&
      vendorInfo?.routingNumber
    );
  };

  const footerButtons = [
    {
      name: "Pay",
      onClick: handlePay,
      disabled: isPayoutLoading || !isVendorInfoComplete(),
      className:
        isPayoutLoading || !isVendorInfoComplete()
          ? "opacity-50 cursor-not-allowed text-white"
          : "text-white",
    },
    {
      name: "Cancel",
      onClick: handleCancel,
      disabled: false,
      className: "bg-white text-gray-900 border",
    },
  ];

  const dialogContent = (
    <div className="w-full">
      {/* Vendor Information */}
      <div className="mb-4">
        <h2 className="text-l text-customGrayDark font-semibold flex gap-2 items-center">
          Payment Details
          <span className="relative group">
            <FiEdit
              onClick={() => handleIconClick(selectedVendor?.id)}
              className="cursor-pointer"
            />
            <div className="absolute z-10 left-0 mt-2 hidden group-hover:block w-56 p-3 bg-white text-sm font-medium text-gray-700 rounded-md shadow-lg border border-gray-200">
              Click here to modify bank account details, routing information,
              and other payment-related settings
            </div>
          </span>
        </h2>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-2">
          {/* Left Column */}
          <div className="flex flex-col">
            <p className="text-sm text-customBlackColor flex items-center mb-2">
              <span className="relative group">
                <MdOutlineStorefront className="mr-2 text-lg" />
                <div className="absolute z-10 left-0 mt-2 hidden group-hover:block w-56 p-3 bg-white text-sm text-gray-700 rounded-md shadow-lg border border-gray-200">
                  Company
                </div>
              </span>
              {vendorInfo?.company || "Enter company name"}
            </p>

            <p className="text-sm text-customBlackColor flex items-center mb-2">
              <span className="relative group">
                <MdOutlineAccountBox className="mr-2 text-lg" />
                <div className="absolute z-10 left-0 mt-2 hidden group-hover:block w-56 p-3 bg-white text-sm text-gray-700 rounded-md shadow-lg border border-gray-200">
                  Account Holder Name
                </div>
              </span>
              {vendorInfo?.accountHolderName || "Enter account holder name"}
            </p>

            <p className="text-sm text-customBlackColor flex items-center mb-2">
              <span className="relative group">
                <MdAccountBalance className="mr-2 text-lg" />
                <div className="absolute z-10 left-0 mt-2 hidden group-hover:block w-56 p-3 bg-white text-sm text-gray-700 rounded-md shadow-lg border border-gray-200">
                  Bank Name
                </div>
              </span>
              {vendorInfo?.bankName || "Enter bank name"}
            </p>

            <p className="text-sm text-customBlackColor flex items-center">
              <span className="relative group">
                <MdOutlinePayment className="mr-2 text-lg" />
                <div className="absolute z-10 left-0 mt-2 hidden group-hover:block w-56 p-3 bg-white text-sm text-gray-700 rounded-md shadow-lg border border-gray-200">
                  Account Number
                </div>
              </span>
              {vendorInfo?.accountNumber || "Enter account number"}
            </p>
          </div>

          <div className="flex flex-col">
            <p className="text-sm text-customBlackColor flex items-center mb-2">
              <span className="relative group">
                <MdOutlineLocalPhone className="mr-2 text-lg" />
                <div className="absolute z-10 left-0 mt-2 hidden group-hover:block w-56 p-3 bg-white text-sm text-gray-700 rounded-md shadow-lg border border-gray-200">
                  Phone
                </div>
              </span>
              {vendorInfo?.countryCode && vendorInfo?.phone
                ? `${vendorInfo.countryCode}${vendorInfo.phone}`
                : "Enter phone number"}
            </p>

            <p className="text-sm text-customBlackColor flex items-center mb-2">
              <span className="relative group">
                <MdOutlineDateRange className="mr-2 text-lg" />
                <div className="absolute z-10 left-0 mt-2 hidden group-hover:block w-56 p-3 bg-white text-sm text-gray-700 rounded-md shadow-lg border border-gray-200">
                  Date of Birth
                </div>
              </span>
              {vendorInfo?.DOB
                ? vendorInfo.DOB instanceof Date
                  ? vendorInfo.DOB.toISOString().split("T")[0]
                  : vendorInfo.DOB
                : "Enter date of birth"}
            </p>

            <p className="text-sm text-customBlackColor flex items-center">
              <span className="relative group">
                <MdRoute className="mr-2 text-lg" />
                <div className="absolute z-10 left-0 mt-2 hidden group-hover:block w-56 p-3 bg-white text-sm text-gray-700 rounded-md shadow-lg border border-gray-200">
                  Routing Number
                </div>
              </span>
              {vendorInfo?.routingNumber || "Enter routing number"}
            </p>
          </div>
        </div>
      </div>

      {/* Order Details */}
      <div className="mb-4 overflow-x-auto">
        <h2 className="text-l text-customGrayDark font-semibold">
          Order Details
        </h2>
        <div className="min-w-full overflow-x-auto">
          <table className="w-full rounded-sm border-collapse">
            <thead className="bg-gray-100 text-gray-500">
              <tr>
                <th className="px-4 font-medium py-2 text-left border-b">
                  Order Number
                </th>
                <th className="px-4 font-medium py-2 text-left border-b">
                  Order Date
                </th>
                <th className="px-4 font-medium py-2 border-b">Total</th>
              </tr>
            </thead>
            <tbody>
              {selectedRows?.map((row) => (
                <tr key={row.id} className="hover:bg-gray-100">
                  <td className="px-4 font-medium py-2 border-b">{row.id}</td>
                  {/* <td className="px-4 font-medium py-2 border-b">1</td> */}
                  <td className="px-4 font-medium py-2 border-b">
                    {row.createdAt?.split("T")[0]}
                  </td>
                  <td className="px-4 font-medium py-2 text-center border-b">
                    $
                    {formatCurrency(row.totalOrderAmount + row.deliveryCharges)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Summary */}
      <div className="flex flex-col items-end rounded-lg justify-end">
        <div className="flex w-full md:w-[50%] items-center h-10 px-4 bg-gray-100">
          <span className="text-l text-customGrayDark font-semibold">
            Summary
          </span>
        </div>
        <div className="px-4 bg-white w-full md:w-[50%] mb-4">
          <div className="flex justify-between text-sm font-medium py-2 border-b">
            <span className="flex-1">SubTotal Amount:</span>
            <span className="text-right">
              ${formatCurrency(totalOrderAmountWithDelivery)}
            </span>
          </div>
          <div className="flex justify-between  text-sm font-medium py-2 border-b">
            <span className="flex-1">Total Service Fees:</span>
            <span className="text-red-500 text-right">
              - ${formatCurrency(totalServiceFee)}
            </span>
          </div>
          <div className="flex justify-between text-sm font-medium py-2 border-b">
            <span className="flex-1">Total Delivery Fees:</span>
            <span className="text-red-500 text-right">
              - ${formatCurrency(totalDeliveryCharges)}
            </span>
          </div>
          <div className="flex justify-between text-sm font-medium py-2 border-b">
            <span className="flex-1">Total Processing Fees:</span>
            <span className="text-red-500 text-right">
              - ${formatCurrency(totalPaymentProcessingFee)}
            </span>
          </div>
          <div className="flex justify-between text-sm font-medium py-2 border-b">
            <span className="flex-1">Total Discount Amount:</span>
            <span className="text-red-500 text-right">
              - ${formatCurrency(totalDiscountAmount)}
            </span>
          </div>
          <div className="flex justify-between text-sm font-medium py-2 border-b">
            <span className="flex-1">Payable Amount:</span>
            <span className="text-right">
              {`$${formatCurrency(totalPayoutAmount)}`}
            </span>
          </div>
        </div>
      </div>
    </div>
  );

  return (
    <>
      <button
        onClick={() => {
          handleGetSelectedRows();
          if (localStorage.getItem("startTime")) {
            localStorage.removeItem("startTime");
          }
        }}
        className="bg-customGreenColor text-white px-4 py-2 rounded-lg text-sm w-full sm:w-auto"
      >
        Payout
      </button>
      <SortupDialog
        isOpen={isDialogOpen}
        onClose={() => setIsDialogOpen(false)}
        header="Payout Invoice"
        footerButtons={footerButtons}
        bodyClassName="w-full min-w-[37vw]"
      >
        {dialogContent}
      </SortupDialog>
    </>
  );
};

export default PayoutInvoiceDialog;
